import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import type { MeView } from "../../api/types";
import { Avatar } from "../../ui/Avatar";
import { Button } from "../../ui/Button";
import { rolesInWords } from "../access";
import { useSignOut } from "../auth";
import { MotionSwitch } from "../StatusBar";
import { WorkspaceSwitch } from "../WorkspaceSwitch";

export interface AvatarMenuProps {
  me: MeView;
  /** Draw the discipline switch in the menu. The bar draws it itself when the person must choose or is in the practice space. */
  withSwitch: boolean;
  /** What needs attention, in words ("the file check failed"). Any of it puts a mark on the avatar and is said in the button's name. */
  problems?: readonly string[];
  /** The product's own facts and status, drawn between the motion choice and sign-out (the status of the files, of the app). */
  children?: ReactNode;
}

/**
 * The person's menu at the right end of the bar: who they are and their roles, the discipline they work in, the motion
 * choice, whatever status the product adds, and sign out. It is a disclosure: its content stays in the page, hidden while the
 * menu is closed, so the status keeps following the live channel and the mark on the avatar can tell when something needs
 * attention. Escape closes it and puts the focus back on its button.
 */
export function AvatarMenu({ me, withSwitch, problems = [], children }: AvatarMenuProps) {
  const { pathname } = useLocation();
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const signOut = useSignOut();
  // The menu is open for the screen it was opened on: going to another screen closes it without an effect.
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const open = openedOn === pathname;
  const close = useCallback(() => setOpenedOn(null), []);

  // A press outside the menu closes it.
  useEffect(() => {
    if (!open) return;
    const onPress = (event: Event) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) close();
    };
    document.addEventListener("pointerdown", onPress);
    return () => document.removeEventListener("pointerdown", onPress);
  }, [open, close]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || !open) return;
    event.preventDefault();
    event.stopPropagation();
    close();
    buttonRef.current?.focus();
  };

  // Tabbing away from the menu closes it.
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (open && next instanceof Element && !event.currentTarget.contains(next)) close();
  };

  const roles = rolesInWords(me);
  const attention = problems.length > 0 ? ` (needs attention: ${problems.join(" and ")})` : "";
  return (
    <div className="avatar-menu" ref={rootRef} data-open={open || undefined} onKeyDown={onKeyDown} onBlur={onBlur}>
      <button
        type="button"
        ref={buttonRef}
        className="avatar-menu__button"
        aria-label={`Account menu, ${me.person.display_name}${attention}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => (open ? close() : setOpenedOn(pathname))}
      >
        <Avatar name={me.person.display_name} decorative />
        {problems.length > 0 && <span className="avatar-menu__alert" aria-hidden="true" />}
      </button>
      <div id={panelId} className="avatar-menu__panel" role="group" aria-label="Account" hidden={!open}>
        <p className="avatar-menu__who">
          <strong>{me.person.display_name}</strong>
          {roles && <span className="muted">{roles}</span>}
        </p>
        {withSwitch && <WorkspaceSwitch me={me} />}
        <MotionSwitch />
        {children}
        <Button variant="quiet" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
